import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase, LogIn } from "lucide-react";
import { useLocalization } from "../hooks/useLocalization";
import { LanguageSwitcher } from "./LanguageSwitcher";

/** Solid white, sticky top bar shared by every public page (home, careers, job detail,
 * application status). Kept deliberately opaque (never transparent-over-hero) so the
 * YOKOLICIOUS wordmark, language switcher, Careers and Staff Login controls stay readable
 * over any background image on the page beneath it. */
export const PublicHeader = ({ showCareersLink = true }) => {
  const { t } = useLocalization();
  const navigate = useNavigate();

  return (
    <header
      data-testid="public-header"
      className="sticky top-0 z-50 w-full bg-white/98 backdrop-blur border-b border-blue-950/10 shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 sm:h-[72px] flex items-center justify-between gap-3">
        <Link to="/" className="shrink-0 flex items-center gap-2" data-testid="public-header-logo-link">
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_yoko-payroll-system/artifacts/2tnlqgwi_chicken.png"
            alt="YOKOLICIOUS eagle mascot holding a chicken wing"
            data-testid="public-header-mascot"
            className="h-9 sm:h-12 w-auto object-contain -my-2"
          />
          <img src="/yokolicious-wordmark.png" alt="YOKOLICIOUS" className="h-7 sm:h-9" />
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher />
          {showCareersLink && (
            <button
              data-testid="public-header-careers-button"
              onClick={() => navigate("/careers")}
              className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-50 border border-amber-300 text-blue-950 text-xs sm:text-sm font-semibold hover:bg-amber-100 active:scale-95 transition-all"
            >
              <Briefcase size={14} />
              {t("nav.careers")}
            </button>
          )}
          <button
            data-testid="public-header-staff-login-button"
            onClick={() => navigate("/staff/login")}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full bg-blue-950 text-white text-xs sm:text-sm font-semibold hover:bg-blue-900 active:scale-95 transition-all whitespace-nowrap"
          >
            <LogIn size={14} />
            <span className="hidden xs:inline">{t("homepage.staffLogin")}</span>
          </button>
        </div>
      </div>
    </header>
  );
};
